<script context="module">
	const expectedNomalStatus = ["OK", "CREATED", "UPDATED", "DELETED"];
</script>

<script lang="ts">
	//UI
	import { AutoComplete } from "@hitit/new_ui/src";
	import SetupCollection from "../components/product/StoreItem/SetupCollection.svelte";
	import AreYouSure from "../components/common/AreYouSure.svelte";
	import Collection from "@hitit/hub/src/v7/components/product/Collection.svelte";
	import ContainerLoading from "@hitit/hub/src/v7/components/common/ContainerLoading.svelte";
	import ActionWithCollections from "../components/product/ActionWithCollections.svelte";
	import DetailViewer from "../components/product/StoreItem/DetailViewer.svelte";
	import EditViewer from "../components/product/StoreItem/EditViewer.svelte";
	import MultipleEditViewer from "../components/product/StoreItem/MultipleEditViewer.svelte";

	//UTILS
	import { api, notifications, t } from "@hitit/hub/src/stores";
	import {
		popup,
		saveStatus,
		handlingCollectionId,
		handlingMulitpleStoreItem,
	} from "../store";
	import { dataLayerPush, getKeyInTranslation, scrollTopBy } from "../utils";
	import { GoBackButton } from "@hitit/new_ui/src";
	import { onDestroy } from "svelte";
	import Category from "../class/StoreItem/Category";
	import StoreItem from "../class/StoreItem/StoreItem";
	import Product from "../class/StoreItem/Product";
	import OptionCollection from "../class/StoreItem/OptionCollection";
	import { scrollBlockRequest } from '../../stores/dom';
	// Types
	import type {
		CategoryData,
		StoreItemData,
		UpdateDisplaysortParam,
	} from "@hitit/api";
	import type { SortableEvent } from "sortablejs";
	type MultidragIndex = { multiDragElement: HTMLElement; index: number };
	type UpdateSingleDisplaysortEventDetail = {
		oldIndex: number;
		newIndex: number;
	};
	type UpdateMultipleDisplaysortEventDetail = {
		oldIndicies: MultidragIndex[];
		newIndicies: MultidragIndex[];
	};
	export let storeId = "";
	export let editable = true;
	// Contstants
	const symbol = Symbol("forScrollBlock")
	// Variables
	let loading = false;
	let product: Product;
	let optionCollection: OptionCollection;
	let selectedItems: StoreItem[] = [];
	let selectedCategoryId = "";
	let editmode = false;
	let multipleSelectMode = false;
	let multipleSelectEdit = false;
	let setupCategory: Category | null = null;

	//Utils
	const mobileSaveHandler = () => {
		if (window.innerWidth < 1024) {
			selectedItems = [];
		}
	};
	//AutoComplete
	const autoCompleteCallback = (storeItem: StoreItem) => {
		const itemId = storeItem.id;
		selectedCategoryId = storeItem.cateogry.getId() || "";
		setTimeout(() => {
			const item = document.getElementById(`${itemId}`);
			if (item) {
				item.classList.add("found");
				setTimeout(() => {
					item.classList.remove("found");
				}, 2000);
			}
		}, 20);
	};
	const autocompleteHandler = (search: string) => {
		const items = product.findOption(search);
		return Promise.resolve(
			items.map((v) => {
				const name =
					getKeyInTranslation(v.getTranslation(), "name", "ko") || "";
				return {
					name,
					callback: () => autoCompleteCallback(v),
				};
			})
		);
	};

	//ActionWithCollection
	const multipleEditHandler = () => {
		const condition = !multipleSelectEdit && selectedItems.length === 0;
		multipleSelectEdit = condition ? multipleSelectEdit : !multipleSelectEdit;
	};

	const collectionTextClickHandler = () => {
		if (!multipleSelectMode) selectedItems = [];
	};

	const multipleModeChangeHandler = () => {
		const reg = /^\|TEMP_ITEM\|/;

		selectedItems = multipleSelectMode
			? []
			: selectedItems.filter((v) => !reg.test(v.id || ""));
		multipleSelectMode = !multipleSelectMode;
	};
	const setupCategoryHandler = (e: { detail: Category }) =>
		(setupCategory = setupCategory === null ? e.detail : null);
	const newItemEvent = (e: { detail: Category }) => {
		const displaySort = e.detail.getLastDisplaysort();
		const arg = StoreItem.convienceArg(
			displaySort === 0 ? 0 : displaySort + 1,
			`|TEMP_ITEM|${Math.random()}`
		);
		selectedItems = [StoreItem.convinienceConstructor(arg, "ko", e.detail)];
	};

	const onItemClickEvent = (e: { detail: StoreItem }) => {
		const { detail } = e;
		selectedItems = [detail];
	};
	const multipleViewerDeleteHandler = (e: { detail: string }) => {
		const id = e.detail;
		selectedItems = selectedItems.filter((v) => v.id !== id);
	};

	// Displaysort Handler
	const categoryDisplaysortHandler = async ({
		detail,
	}: {
		detail: UpdateSingleDisplaysortEventDetail;
	}) => {
		try {
			$saveStatus.storeItems = true;
			const { oldIndex, newIndex } = detail;
			if (oldIndex === newIndex) return;

			const updatedCategories: UpdateDisplaysortParam =
				product.updateDisplaysort(oldIndex, newIndex);

			const { data } = await $api.setCategoryDisplaysort(
				storeId,
				updatedCategories
			);

			const notNormalStatus =
				expectedNomalStatus.find((v) => v === data.status) === undefined;
			if (notNormalStatus) throw new Error(data.status);
			product = product;
			//
		} catch (error) {
			errorHandler(error as Error);
		} finally {
			$saveStatus.storeItems = false;
		}
	};
	const itemDisplaysortHandler = async ({
		detail,
	}: {
		detail: UpdateSingleDisplaysortEventDetail;
	}) => {
		try {
			$saveStatus.storeItems = true;
			const { oldIndex, newIndex } = detail;
			if (oldIndex === newIndex) return;
			if (selectedCategory == undefined) return;
			const updatedItems: UpdateDisplaysortParam =
				selectedCategory.updateDisplaysort(oldIndex, newIndex);

			const { data } = await $api.setStoreitemDisplaysort(
				storeId,
				updatedItems
			);

			const notNormalStatus =
				expectedNomalStatus.find((v) => v === data.status) === undefined;
			if (notNormalStatus) throw new Error(data.status);
			product = product;
			selectedCategory = selectedCategory;
			selectedCategoryId = selectedCategoryId;
			//
		} catch (error) {
			errorHandler(error as Error);
		} finally {
			$saveStatus.storeItems = false;
		}
	};
	const itemUpdateCollection = async ({
		detail,
	}: {
		detail: { categoryIndex: number };
	}) => {
		try {
			$saveStatus.storeItems = true;
			const { categoryIndex } = detail;
			const category = product.getCategories()[categoryIndex];

			if (multipleSelectMode) {
				if ($handlingMulitpleStoreItem === false) return;

				const originalCategories = new Set(
					selectedItems.map((v) => v.cateogry.getId())
				);
				const joinedOriginalCategoriesId = [...originalCategories].join("");
				if (joinedOriginalCategoriesId === category.getId()) {
					return;
				}
				const updatedItems = await updateItemCollections(
					category,
					selectedItems
				);
				const newSelectedItems = selectedItems.map((v) => {
					return updatedItems.find((uv) => uv.uid === v.uid) as StoreItem;
				});
				selectedItems = newSelectedItems;
			} else {
				const noneId = $handlingCollectionId === undefined;
				const findItem = selectedCategory?.findItemById($handlingCollectionId);
				const sameCategory = findItem?.cateogry.getId() === category.getId();
				if (noneId || findItem === undefined || sameCategory) return;
				await updateItemCollections(category, [findItem]);
				selectedCategory?.deleteItemByUid(findItem.uid || "");

				selectedItems = selectedItems.filter((v) => v.uid !== findItem.uid);
			}

			product = product;
		} catch (error) {
			errorHandler(error as Error);
		} finally {
			$saveStatus.storeItems = false;
		}
	};

	const updateItemCollections = async (
		toCategory: Category,
		items: StoreItem[]
	) => {
		const updatedItems: StoreItem[] = [];
		for (const item of items) {
			if (item.cateogry.getId() === toCategory.getId()) {
				updatedItems.push(item);
				continue;
			}

			item.cateogry.deleteItemByUid(item.uid || "");
			const categoryId = toCategory?.getId();
			const displaySort = toCategory.getLastDisplaysort() + 1;
			const updatedDTO = Object.assign({}, item.getStoreItemDTO(), {
				displaySort,
			});
			const [, , newItem] = await updateSingleItem(
				updatedDTO,
				categoryId || ""
			);
			const storeItem = new StoreItem(newItem, toCategory);
			toCategory.replaceStoreItemByUid(storeItem, "");
			updatedItems.push(storeItem);
		}
		return updatedItems;
	};

	const updateMultipleItemDisplaySort = async ({
		detail,
	}: {
		detail: UpdateMultipleDisplaysortEventDetail;
	}) => {
		try {
			if ($saveStatus.storeItems) return;

			$saveStatus.storeItems = true;
			const { oldIndicies, newIndicies } = detail;

			const hasNotSelectedCategory = selectedCategory == null;
			const lengthIsZero = oldIndicies.length === 0 || newIndicies.length === 0;
			const oldIndexies = oldIndicies.map((v) => v.index);
			const newIndexies = newIndicies.map((v) => v.index);
			if (hasNotSelectedCategory || lengthIsZero) return;
			const updatedItems = selectedCategory!.updateMultipleDisplaySort(
				oldIndexies,
				newIndexies
			);

			const { data } = await $api.setStoreitemDisplaysort(
				storeId,
				updatedItems
			);

			const notNormalStatus =
				expectedNomalStatus.find((v) => v === data.status) === undefined;
			if (notNormalStatus) throw new Error(data.status);
			product = product;
			selectedCategory = selectedCategory;
			selectedCategoryId = selectedCategoryId;
			selectedItems = selectedItems.map((v) => {
				const category = v.cateogry;
				return category.findItemByUid(v.uid || "") as StoreItem;
			});

			$saveStatus.storeItems = false;
		} catch (error) {
			errorHandler(error as Error);
			$saveStatus.storeItems = false;
		}
	};

	const collectionSortHandler = (start: boolean) => {
		return (e: SortableEvent) => {
			if (multipleSelectMode) {
				//
				$handlingMulitpleStoreItem = start ? true : false;
			} else {
				$handlingCollectionId = start ? e.item.id : undefined;
			}
		};
	};

	//PopupHandler
	const categoryDeletePopupHandler = (e: { detail: Category }) => {
		$popup = {
			component: AreYouSure,
			props: {
				textTranslation: "global.product.category.you_want_to_delete_category",
				accept: () => deleteCollection(e.detail),
				cancel: () => ($popup = undefined),
			},
		};
	};
	const singleStoreItemDeleteHanlder = (e: { detail: StoreItem }) => {
		$popup = {
			component: AreYouSure,
			props: {
				textTranslation: "global.product.category.you_want_to_delete_item",
				accept: () => deleteSingleItem(e.detail),
				cancel: () => ($popup = undefined),
			},
		};
	};
	const multipleDeleteHandler = () => {
		$popup = {
			component: AreYouSure,
			props: {
				textTranslation: "global.product.category.you_want_to_delete_item",
				accept: () => deleteMultipleItem(selectedItems),
				cancel: () => ($popup = undefined),
			},
		};
	};

	//Create, Update
	const singleStoreItemEditHandler = async (e: { detail: StoreItem }) => {
		try {
			$saveStatus.storeItems = true;
			const editItem = e.detail;
			const original = selectedCategory!
				.getStoreItems()
				.find((v) => v.id === e.detail.id);

			const isNew = editItem.uid === undefined;
			const id = original?.id;
			const displaySort = isNew
				? selectedCategory!.getLastDisplaysort() + 1
				: original?.displaySort;
			const collectionId = editItem.cateogry.getId() || "";
			const dto = Object.assign({}, editItem.getStoreItemDTO(), {
				id,
				displaySort,
			});

			const [, uid, storeItemDTO] = await updateSingleItem(dto, collectionId);
			const newItem = new StoreItem(storeItemDTO, editItem.cateogry);
			selectedCategory!.replaceStoreItemByUid(newItem, uid);

			product = product;
			selectedItems = isNew ? [] : [newItem];
			dataLayerPush(
				id === undefined ? "CREATE_NEW_STOREITEM" : "UPDATE_STOREITEM"
			);
			mobileSaveHandler();
		} catch (error) {
			errorHandler(error as Error);
		} finally {
			$saveStatus.storeItems = false;
		}
	};

	const addNewCollection = async (e: { detail: string }) => {
		try {
			$saveStatus.storeItems = true;
			const categories = product.getCategories();
			const lastDisplaySort =
				categories.length > 0
					? categories[categories.length - 1].displaySort + 1
					: 0;
			const category = Category.convinienceConstructor(
				e.detail,
				lastDisplaySort
			);
			const [, id, categoryDTO] = await updateCollection(
				category.getCategoryDTO()
			);

			const newCategory = new Category(categoryDTO);
			product.addNewCategory(newCategory);
			selectedItems = [];
			selectedCategoryId = id;
			product = product;
			dataLayerPush("CREATE_NEW_CATEGORY");
		} catch (error) {
			errorHandler(error as Error);
			//
		} finally {
			$saveStatus.storeItems = false;
		}
	};
	const setCategoryUpdateHandler = async ({
		detail,
	}: {
		detail: CategoryData;
	}) => {
		try {
			$saveStatus.options = true;
			//
			const dto = detail;
			const originId = dto.id || "";
			const [, , categoryDTO] = await updateCollection(dto);
			const newCategory = new Category(categoryDTO);
			product.replaceCategoryById(originId, newCategory);
			product = product;
			dataLayerPush("CATEGORY_UPDATED");
		} catch (error) {
			errorHandler(error as Error);
		} finally {
			$saveStatus.options = false;
		}
	};
	const multipleViewerSaveHandler = async (e: { detail: StoreItem[] }) => {
		try {
			$saveStatus.storeItems = true;
			for (const item of e.detail) {
				const categoryId = item.cateogry.getId() || "";
				const dto = item.getStoreItemDTO();
				const category = item.cateogry;
				const [, uid, storeItemDTO] = await updateSingleItem(dto, categoryId);
				const newStoreItem = new StoreItem(storeItemDTO, category);
				category.replaceStoreItemByUid(newStoreItem, uid);
			}
			product = product;
			selectedItems = selectedItems.map((v) => {
				const originCategory = v.cateogry;
				const findItem = originCategory.findItemByUid(v.uid || "") as StoreItem;
				return findItem;
			});
			mobileSaveHandler();
		} catch (error) {
			errorHandler(error as Error);
		} finally {
			$saveStatus.storeItems = false;
		}
	};
	//Delete
	const deleteCollection = async (target: Category) => {
		try {
			$popup = undefined;
			$saveStatus.storeItems = true;
			const { status } = (await $api.deleteCategory(target.getId() || "")).data;
			const throwErrorCondition =
				expectedNomalStatus.find((v) => status === v) === undefined;
			if (throwErrorCondition) throw new Error(status);
			product.deleteCategory(target);
			selectedItems = [];
			dataLayerPush("DELETE_CATEGORY");
			product = product;
		} catch (error) {
			errorHandler(error as Error);
		} finally {
			$saveStatus.storeItems = false;
		}
	};

	const deleteSingleItem = async (storeItem: StoreItem) => {
		try {
			$popup = undefined;
			$saveStatus.storeItems = true;
			const uid = storeItem.uid || "";

			await deleteStoreItem(uid);
			storeItem.cateogry.deleteItemByUid(uid);
			selectedItems = selectedItems.filter((v) => v.uid === uid);
			product = product;
		} catch (error) {
			errorHandler(error as Error);
			//
		} finally {
			$saveStatus.storeItems = false;
		}
	};

	const deleteMultipleItem = async (storeItems: StoreItem[]) => {
		try {
			$popup = undefined;
			$saveStatus.storeItems = true;
			for (const item of storeItems) {
				const uid = item.uid || "";
				await deleteStoreItem(uid);
				item.cateogry.deleteItemByUid(uid);
			}
			selectedItems = [];
			product = product;
		} catch (error) {
			errorHandler(error as Error);
		} finally {
			$saveStatus.storeItems = false;
		}
	};

	//API
	const updateCollection = async (category: CategoryData) => {
		try {
			const setCategoryResponse = await $api.setStoreCategoryData(
				storeId,
				category
			);
			const getCategoryResponse = await $api.getStoreItems(storeId);
			const { uid, id, status } = setCategoryResponse.data;
			const newCategoryData = getCategoryResponse.data.categoryDTOs.find(
				(v) => v.id === id
			);
			const throwErrorCondition =
				newCategoryData === undefined ||
				expectedNomalStatus.find((v) => status === v) === undefined;

			if (throwErrorCondition) throw new Error(status);
			dataLayerPush("UPDATE_CATEGORY");
			return [uid, id, newCategoryData] as [string, string, CategoryData];
		} catch (e) {
			throw e;
		}
	};
	const deleteStoreItem = async (storeItemUid: string) => {
		try {
			const { status } = (await $api.deleteStoreItem(storeItemUid)).data;
			const throwErrorCondition =
				expectedNomalStatus.find((v) => status === v) === undefined;
			if (throwErrorCondition) throw new Error(status);
			dataLayerPush("DELETE_STOREITEM");
			return storeItemUid;
		} catch (e) {
			throw e;
		}
	};

	const updateSingleItem = async (
		itemDTO: StoreItemData,
		categoryId: string
	) => {
		try {
			const { id, uid, status } = await (
				await $api.setStoreItem(storeId, categoryId, itemDTO)
			).data;
			const newItem = Object.assign({}, itemDTO, { id, uid });
			const throwErrorCondition =
				expectedNomalStatus.find((v) => status === v) === undefined;

			if (throwErrorCondition) throw Error(status);

			return [id, uid, newItem] as [string, string, StoreItemData];
			//
		} catch (error) {
			throw error;
		}
	};
	//self
	async function editmodeHandler() {
		if (!editable) return;
		editmode = !editmode;
	}

	const errorHandler = async (error: Error) => {
		const message = error.message;
		const errorTranslation = $t(`error.DATA_WAS_BEEN_MODIFIED`);
		const errorMessage = `
			ERROR STATUS : ${message},<br>
			${errorTranslation}
		`;
		dataLayerPush("MANAGE_STORE_ITEM_API_FAILED", message);
		notifications.append("ERROR", errorMessage, "", undefined, "ERROR");
		console.log(error);

		await getStoreItem(storeId);
	};

	async function getStoreItem(storeId: string) {
		try {
			loading = true;

			const [categoryResponse, optionResponse] = await Promise.all([
				$api.getStoreItems(storeId),
				$api.getStoreOptionCollection(storeId),
			]);
			const { categoryDTOs } = categoryResponse.data;
			const { optionCollectionDTOs } = optionResponse.data;
			product = new Product(categoryDTOs, categoryResponse.data.revision);
			optionCollection = new OptionCollection(
				optionCollectionDTOs,
				optionResponse.data.revision
			);
			selectedCategoryId = product
				.getNameIdItemLength()
				.reduce((prev, cur, i) => {
					if (i === 0) return cur.id || "";

					return prev;
				}, "");
		} catch (e) {
			console.log(e);
		} finally {
			loading = false;
		}
	}

	$: getStoreItem(storeId);
	$: selectedCategory = product
		? product.getCategories().find((v) => v.getId() === selectedCategoryId)
		: null;
	$: detailDisplayCondition =
		(!editmode && selectedItems.length === 1) ||
		(editmode && !multipleSelectMode && selectedItems.length === 1) ||
		(editmode &&
			multipleSelectMode &&
			selectedItems.length >= 1 &&
			multipleSelectEdit);

	$: detailDisplayCondition ? scrollBlockRequest.blockRequest(symbol) : scrollBlockRequest.removeRequest(symbol);

	onDestroy(() => {
		$popup = undefined;
		scrollBlockRequest.removeRequest(symbol)
	});
</script>

<div class="container">
	{#if loading}
		<ContainerLoading />
	{/if}
	{#if product}
		<form id="search-item" class:mobileHide={detailDisplayCondition}>
			<AutoComplete
				placeholder={$t("global.product.category.find_item")}
				searchCallback={autocompleteHandler}
			/>
		</form>
		<div class="actions" class:mobileHide={detailDisplayCondition}>
			<ActionWithCollections
				placeholder={$t("global.product.category.category_name")}
				on:EDIT_CHANGE={editmodeHandler}
				on:MULTIPLE_DELETE={multipleDeleteHandler}
				on:MULTIPLE_SELECT_MODE_CHANGE={multipleModeChangeHandler}
				on:MULTIPLE_EDIT={multipleEditHandler}
				on:CLICK_COLLECTION_TEXT={collectionTextClickHandler}
				on:UPDATED_COLLECTION_DISPLAY_SORT={categoryDisplaysortHandler}
				on:NEW_COLLECTION={addNewCollection}
				on:INPUT_HARDWARE_UP_EVENT={itemUpdateCollection}
				bind:editmode={editmode}
				bind:multipleSelectMode={multipleSelectMode}
				bind:selectedCollectionUid={selectedCategoryId}
				sortDisable={editmode === false}
				editable={editable}
				collections={product.getNameIdItemLength()}
			/>
		</div>
		<div class="list-detail-wrapper">
			<div class="list" class:mobileHide={detailDisplayCondition}>
				{#if selectedCategory}
					<Collection
						on:COLLECTION_SETUP_EVENT={setupCategoryHandler}
						on:ITEM_CLICK_EVENT={onItemClickEvent}
						on:ITEM_DELETE_CLICK_EVENT={singleStoreItemDeleteHanlder}
						on:NEW_ITEM_EVENT={newItemEvent}
						on:UPDATED_ITEM_DISPLAY_SORT={itemDisplaysortHandler}
						on:UPDATED_MULTIPLE_ITEM_DISPLAY_SORT={updateMultipleItemDisplaySort}
						bind:selectedItems={selectedItems}
						afterSortingstart={collectionSortHandler(true)}
						afterSortingend={collectionSortHandler(false)}
						hasSetup={Boolean(setupCategory)}
						collection={selectedCategory}
						items={selectedCategory?.getStoreItems()}
						editmode={editmode}
						sortDisable={editmode === false}
						multipleSelectMode={multipleSelectMode}
					>
						<div slot="setup">
							{#if setupCategory}
								<SetupCollection
									on:UPDATE_CATEGORY={setCategoryUpdateHandler}
									on:DELETE_CATEGORY={categoryDeletePopupHandler}
									category={selectedCategory}
									clickOutsideCallBack={() => (setupCategory = null)}
								/>
							{/if}
						</div>
					</Collection>
				{/if}
			</div>
			<div
				class="detail"
				use:scrollTopBy={detailDisplayCondition}
				class:mobileDisplayCondition={detailDisplayCondition}
			>
				<div class="goBack">
					<GoBackButton
						on:click={() => {
							selectedItems = [];
							multipleSelectEdit = false;
						}}
					/>
				</div>
				{#if !editmode && selectedItems.length === 1}
					<DetailViewer item={selectedItems[0]} />
				{:else if editmode && !multipleSelectMode && selectedItems.length === 1}
					<EditViewer
						on:EDIT_ITEM_DONE={singleStoreItemEditHandler}
						item={selectedItems[0].getClone()}
						optionCollections={optionCollection.categories}
					/>
				{:else if editmode && multipleSelectMode && selectedItems.length >= 1}
					<MultipleEditViewer
						items={selectedItems.map((v) => v.getClone())}
						on:UPDATE_MULTIPLE_ITEMS={multipleViewerSaveHandler}
						on:MULTIPLE_SELECTED_ITEM_DELETE={multipleViewerDeleteHandler}
					/>
				{/if}
			</div>
		</div>
	{/if}
</div>

<style lang="scss">
	@import "../../variables.scss";

	.container {
		display: flex;
		flex-direction: column;
		width: 100%;
		gap: 22px;

		#search-item {
			width: 100%;
			height: 31px;
			color: #46474b;
		}

		.mobileHide {
			display: none;
		}
		.detail {
			display: none;
			position: fixed;
			top: 0;
			bottom: 0;
			left: 0;
			right: 0;
			/* min-height: 100vh;
			max-height: 100vh; */
			overflow-y: auto;
			background-color: #f0f0f4;
			padding: 70px 20px 20px 20px;
			box-sizing: border-box;
			&.mobileDisplayCondition {
				display: flex;
				flex-direction: column;
			}
		}

		.goBack {
			width: 30px;
			height: 27px;
			position: absolute;
			top: 23.2px;
			right: 20px;
		}
	}
	@include DesktopSize {
		.container {
			#search-item {
				width: calc(50% - 15.5px);
				font-size: 1.25rem;
				height: 40px;
			}

			.mobileHide {
				display: block;
			}
			.list-detail-wrapper {
				flex-basis: 100%;
				width: 100%;
				display: flex;
				gap: 20px;
				.list {
					flex-basis: calc(100% * (952 / 1594));
					flex-grow: 1;
				}
			}
			.detail {
				padding: 0;
				display: flex;
				flex-direction: column;
				position: static;
				min-height: 0;
				max-height: none;
				flex-basis: calc(100% * (642 / 1594));
				max-width: 642px;
				/* temp debuging point */
			}
			.goBack {
				display: none;
			}
		}
	}
</style>
